/**
 * The inactivity tracker: the 4-hour wake check (STEP_6_SPEC.md D8, D9).
 *
 * A plain service the container creates and tears down. It owns exactly three
 * things and nothing else:
 *
 *   1. Seed — the local interaction clock starts at the SESSION CELL's
 *      `last_activity_at`, never at `Date.now()`. This is what makes five hours
 *      away five hours away even across a reload: a tracker seeded at mount
 *      time would find "the user is present" true the instant the page loads.
 *   2. Listen — `pointerdown` and `keydown` on `window` move the local clock
 *      forward. That is the whole interaction model: a variable, no IndexedDB
 *      write, no `lamport` bump, no Channel 2 POST. Scroll/wheel are excluded
 *      deliberately — reading without touching is inactivity by the docs' own
 *      word ("without user interaction", ARCHITECTURE.md §5.1).
 *   3. Evaluate — on start (launch) and on `visibilitychange` arriving at
 *      `visible`. No interval timer, no `online` handler (a reconnect is not a
 *      user waking), no periodic re-check while visible.
 *
 * The wake rule is the one extension REPLICATION.md §5.5's letter needed:
 *
 *     fire = now - max(cell.last_activity_at, lastInteraction) >= 4 hours
 *
 * Without the `max()`, a capture-heavy user — whose quick captures and J/K
 * moves author no session patch — would be shown a checkpoint for activity the
 * cell cannot see. On fire, while `status !== 'TimedOut'`, the mark is
 * `store.updateSession({ status: 'TimedOut' })` — LOCAL ONLY (D10). It
 * persists to the IndexedDB `session` store and never reaches the relay: the
 * mark is a derivation every device makes on its own wake, and a published
 * `TimedOut` cell could out-`lamport` a live second device into a false
 * checkpoint. The status precondition is also why the mark refreshing its own
 * `last_activity_at` stamp is harmless: it can never re-fire from the stamp it
 * just wrote (Review Focus #1).
 *
 * It never renders, never touches the view, and exposes no events — the
 * container learns of a timeout by observing `session.status`, the same signal
 * everything else reads.
 */

import { FOUR_HOURS_MILLIS, type NotebookStore } from '../storage/operations';

export interface InactivityTracker {
  /** Installs listeners, seeds from the cell, runs the launch check. */
  start(): Promise<void>;
  /** Removes listeners. Idempotent. */
  stop(): void;
}

export function createInactivityTracker(store: NotebookStore): InactivityTracker {
  /** Local variable only — never the session cell (D8). */
  let lastInteraction = 0;
  let started = false;

  async function evaluate(): Promise<void> {
    const cell = await store.loadSession();
    if (!cell) return;
    const fire =
      Date.now() - Math.max(cell.last_activity_at, lastInteraction) >=
      FOUR_HOURS_MILLIS;
    if (!fire) return;
    // The precondition is what keeps the mark from re-firing off its own
    // refreshed stamp; when already TimedOut there is nothing to write.
    if (cell.status !== 'TimedOut') {
      await store.updateSession({ status: 'TimedOut' });
    }
  }

  const onInteraction = () => {
    lastInteraction = Date.now();
  };

  const onVisibility = () => {
    // Only arriving at visible is a wake. Hiding evaluates nothing.
    if (document.visibilityState !== 'visible') return;
    void evaluate();
  };

  return {
    async start() {
      if (started) return;
      started = true;

      // Seed BEFORE the launch check, from the cell — never `Date.now()`.
      const cell = await store.loadSession();
      lastInteraction = cell?.last_activity_at ?? 0;

      window.addEventListener('pointerdown', onInteraction);
      window.addEventListener('keydown', onInteraction);
      document.addEventListener('visibilitychange', onVisibility);

      await evaluate();
    },

    stop() {
      if (!started) return;
      started = false;
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
